/**
 * Render an order QR as inline SVG markup for packing-slip HTML.
 * Uses qrcode.react off-DOM (browser only).
 */
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { formatOrderQrPayload } from './orderQrPayload';

const DEFAULT_SIZE = 96;

/**
 * @param {string|number} sourceId
 * @param {{ size?: number }} [opts]
 * @returns {Promise<string>} SVG outerHTML or empty string
 */
export async function renderOrderQrSvgMarkup(sourceId, opts = {}) {
  const value = formatOrderQrPayload(sourceId);
  if (!value) return '';

  const size = Number(opts.size) > 0 ? Number(opts.size) : DEFAULT_SIZE;
  return renderToStaticMarkup(
    React.createElement(QRCodeSVG, {
      value,
      size,
      width: size,
      height: size,
      level: 'M',
      includeMargin: false,
      bgColor: '#ffffff',
      fgColor: '#000000',
      xmlns: 'http://www.w3.org/2000/svg',
    }),
  );
}

/**
 * @param {Array} orders
 * @param {(order: object) => string} getSourceId
 * @param {{ size?: number }} [opts]
 * @returns {Promise<Map<string, string>>} sourceId -> svg markup
 */
export async function renderOrderQrSvgMap(orders, getSourceId, opts = {}) {
  const list = Array.isArray(orders) ? orders : [];
  const map = new Map();
  const unique = new Map();
  const size = Number(opts.size) > 0 ? Number(opts.size) : DEFAULT_SIZE;

  list.forEach((order) => {
    const id = getSourceId(order);
    if (id && !unique.has(id)) unique.set(id, order);
  });

  await Promise.all(
    Array.from(unique.keys()).map(async (id) => {
      const svg = await renderOrderQrSvgMarkup(id, { size });
      if (svg) map.set(id, svg);
    }),
  );

  return map;
}
